/**
 * Turn circle junctions from streamed junction dwells.
 *
 * The live replica materializes every `junction_wait` frame as a zero-speed
 * playback leg whose id is `junction-wait:<ohtId>:<nodeId>:<simTimeS>`. That
 * leg is the authoritative statement that the vehicle stands still at the node
 * for a known window, so the disk is swung onto the exit axis inside the same
 * window and the OHT rolls on the moment it closes — no browser-side wait is
 * added on top of the backend's.
 */
import type { LayoutElement } from '../types';
import type { BrowserKineticVehicleLeg } from '../types/simulationPlayback';
import { shortestAngleDeltaDeg } from '../store/junctionTurntableStore';
import {
  getConfiguredTurntableBarAngleDeg,
  getTurntableEngageRadius,
  getTurntableHubWorld,
  getTurntablePortAngleDeg,
  isTurntableAnimationEnabled,
  resolveTurntableExitPort,
  resolveTurntableTargetPort,
  shouldLockTurntableBarForPorts,
} from './junctionTurntableSim';
import { getCircleJunctionActivePortIndices, resolveRailType } from './topology';

export const JUNCTION_WAIT_LEG_PREFIX = 'junction-wait:';

/** Share of the dwell spent swinging the bar onto the entry axis. */
const ALIGN_FRACTION = 0.35;
/** Below this the two endpoints are the same point and carry no heading. */
const MIN_LEG_LENGTH = 1e-6;

export type JunctionWaitLegRef = {
  legId: string;
  ohtId: string;
  nodeId: string;
  startS: number;
};

export type StreamedJunctionTurn = {
  legId: string;
  ohtId: string;
  junctionId: string;
  nodeId: string;
  startS: number;
  endS: number;
  /** 0..1 across the streamed dwell. */
  progress01: number;
  fromPort: number;
  toPort: number;
  /** Element-local disk angle for this instant; equals the exit axis at 1. */
  barAngleDeg: number;
};

/** A dwell leg is recognised by the id the replica stamps on it. */
export function isJunctionWaitLeg(
  leg: Pick<BrowserKineticVehicleLeg, 'legId' | 'reason'>
): boolean {
  return (
    (typeof leg.legId === 'string' &&
      leg.legId.startsWith(JUNCTION_WAIT_LEG_PREFIX)) ||
    leg.reason === 'junctionDwell'
  );
}

/**
 * Split `junction-wait:<ohtId>:<nodeId>:<simTimeS>`. Node ids may be blank on
 * older frames, so only the OHT and the stamp are required.
 */
export function parseJunctionWaitLegId(legId: string): JunctionWaitLegRef | null {
  if (!legId.startsWith(JUNCTION_WAIT_LEG_PREFIX)) return null;
  const parts = legId.slice(JUNCTION_WAIT_LEG_PREFIX.length).split(':');
  if (parts.length < 3) return null;
  const startS = Number(parts[parts.length - 1]);
  const ohtId = parts[0];
  if (!ohtId || !Number.isFinite(startS)) return null;
  return {
    legId,
    ohtId,
    nodeId: parts.slice(1, -1).join(':'),
    startS,
  };
}

function legHeadingDeg(leg: BrowserKineticVehicleLeg): number | null {
  const dx = leg.toX - leg.fromX;
  const dy = leg.toY - leg.fromY;
  if (Math.hypot(dx, dy) <= MIN_LEG_LENGTH) return null;
  return (Math.atan2(dy, dx) * 180) / Math.PI;
}

/** Heading the vehicle arrives on: the last moving leg that ends by `startS`. */
function approachHeadingDeg(
  legs: readonly BrowserKineticVehicleLeg[],
  startS: number
): number | null {
  let best: { endS: number; heading: number } | null = null;
  for (const leg of legs) {
    if (leg.endS > startS + MIN_LEG_LENGTH) continue;
    const heading = legHeadingDeg(leg);
    if (heading == null) continue;
    if (!best || leg.endS > best.endS) best = { endS: leg.endS, heading };
  }
  return best?.heading ?? null;
}

/** Heading the vehicle leaves on: the first moving leg starting at/after `endS`. */
function departureHeadingDeg(
  legs: readonly BrowserKineticVehicleLeg[],
  endS: number
): number | null {
  let best: { startS: number; heading: number } | null = null;
  for (const leg of legs) {
    if (leg.startS < endS - MIN_LEG_LENGTH) continue;
    const heading = legHeadingDeg(leg);
    if (heading == null) continue;
    if (!best || leg.startS < best.startS) best = { startS: leg.startS, heading };
  }
  return best?.heading ?? null;
}

function smoothstep01(value: number): number {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
}

/** Circle junction whose hub is closest to the dwell point and in engage range. */
function junctionAtWaitPoint(
  junctions: readonly LayoutElement[],
  point: { x: number; y: number }
): LayoutElement | null {
  let best: { element: LayoutElement; distance: number } | null = null;
  for (const element of junctions) {
    const hub = getTurntableHubWorld(element);
    const distance = Math.hypot(point.x - hub.x, point.y - hub.y);
    if (distance > getTurntableEngageRadius(element)) continue;
    if (!best || distance < best.distance) best = { element, distance };
  }
  return best?.element ?? null;
}

/**
 * Disk angle for a dwell: onto the entry axis first, then round to the exit,
 * landing exactly on the exit axis as the dwell closes.
 *
 * Angles are derived from canonical port axes rather than the last rendered
 * angle, so scrubbing to any time reproduces the same pose.
 */
/**
 * OHT heading during a streamed junction dwell — stays on the entry axis while
 * the disk pre-aligns, then sweeps toward the exit with the rotating bar.
 */
export function junctionTurnOhtAngleDeg(
  element: LayoutElement,
  fromPort: number,
  toPort: number,
  progress01: number,
  rails: readonly LayoutElement[] = []
): number {
  const entryBar = getTurntablePortAngleDeg(element, fromPort);
  const headingInDeg = entryBar + 180;
  const headingDeltaDeg = shortestAngleDeltaDeg(
    headingInDeg,
    getTurntablePortAngleDeg(element, toPort)
  );
  if (shouldLockTurntableBarForPorts(element, fromPort, toPort, rails)) {
    return headingInDeg + (element.rotation || 0);
  }
  if (progress01 < ALIGN_FRACTION) {
    return headingInDeg + (element.rotation || 0);
  }
  const rotateT = smoothstep01((progress01 - ALIGN_FRACTION) / (1 - ALIGN_FRACTION));
  return headingInDeg + headingDeltaDeg * rotateT + (element.rotation || 0);
}

export function junctionTurnBarAngleDeg(
  element: LayoutElement,
  fromPort: number,
  toPort: number,
  progress01: number,
  rails: readonly LayoutElement[] = []
): number {
  if (shouldLockTurntableBarForPorts(element, fromPort, toPort, rails)) {
    return getConfiguredTurntableBarAngleDeg(element, rails);
  }
  const baseBar = getTurntablePortAngleDeg(element, 0);
  const entryBar = getTurntablePortAngleDeg(element, fromPort);
  const headingIn = entryBar + 180;
  const exitBar =
    entryBar +
    shortestAngleDeltaDeg(headingIn, getTurntablePortAngleDeg(element, toPort));

  if (progress01 < ALIGN_FRACTION) {
    return (
      baseBar +
      shortestAngleDeltaDeg(baseBar, entryBar) *
        smoothstep01(progress01 / ALIGN_FRACTION)
    );
  }
  return (
    entryBar +
    shortestAngleDeltaDeg(entryBar, exitBar) *
      smoothstep01((progress01 - ALIGN_FRACTION) / (1 - ALIGN_FRACTION))
  );
}

/** OHTs the backend is timing at junctions, by presence of any dwell leg. */
export function backendTimedJunctionOhtIds(
  legsByVehicle: Record<string, readonly BrowserKineticVehicleLeg[]>
): Set<string> {
  const ids = new Set<string>();
  for (const [ohtId, legs] of Object.entries(legsByVehicle)) {
    if (legs.some(isJunctionWaitLeg)) ids.add(ohtId);
  }
  return ids;
}

/**
 * Disk turns owed at `simTimeS`, one per OHT currently inside a streamed dwell.
 */
export function resolveStreamedJunctionTurns(params: {
  simTimeS: number;
  simTimeByOhtId?: Readonly<Record<string, number>>;
  legsByVehicle: Record<string, readonly BrowserKineticVehicleLeg[]>;
  elements: readonly LayoutElement[];
}): StreamedJunctionTurn[] {
  const junctions = params.elements.filter(
    (element) =>
      resolveRailType(element) === 'junction-circle' &&
      isTurntableAnimationEnabled(element)
  );
  if (junctions.length === 0) return [];

  const turns: StreamedJunctionTurn[] = [];
  const claimed = new Set<string>();

  for (const [vehicleId, legs] of Object.entries(params.legsByVehicle)) {
    const vehicleTime = params.simTimeByOhtId?.[vehicleId] ?? params.simTimeS;
    const wait = legs.find(
      (leg) =>
        isJunctionWaitLeg(leg) &&
        vehicleTime >= leg.startS &&
        vehicleTime <= leg.endS
    );
    if (!wait?.legId) continue;
    const ref = parseJunctionWaitLegId(wait.legId) ?? {
      legId: wait.legId,
      ohtId: wait.vehicleId || vehicleId,
      nodeId: wait.fromId,
      startS: wait.startS,
    };

    const element = junctionAtWaitPoint(junctions, { x: wait.fromX, y: wait.fromY });
    // One disk cannot serve two dwells; the first vehicle in owns it.
    if (!element || claimed.has(element.id)) continue;

    const activePorts = getCircleJunctionActivePortIndices(element, params.elements);
    const approach = approachHeadingDeg(legs, wait.startS);
    if (approach == null) continue;
    const fromPort = resolveTurntableTargetPort(element, approach, activePorts);
    if (fromPort == null) continue;

    const departure = departureHeadingDeg(legs, wait.endS);
    const toPort =
      departure == null
        ? fromPort
        : resolveTurntableExitPort(
            element,
            departure,
            fromPort,
            activePorts,
            params.elements
          );

    const durationS = wait.endS - wait.startS;
    const progress01 =
      durationS > 0
        ? Math.max(0, Math.min(1, (vehicleTime - wait.startS) / durationS))
        : 1;

    claimed.add(element.id);
    turns.push({
      legId: wait.legId,
      ohtId: ref.ohtId,
      junctionId: element.id,
      nodeId: ref.nodeId || wait.fromId,
      startS: wait.startS,
      endS: wait.endS,
      progress01,
      fromPort,
      toPort,
      barAngleDeg: junctionTurnBarAngleDeg(
        element,
        fromPort,
        toPort,
        progress01,
        params.elements
      ),
    });
  }

  return turns;
}
